import React from 'react';
import { useSalesTransactionsPageQuery } from '@/api/queries/sales';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { SalesTransaction } from '@/api/models';
import {
    getPaginationWindow,
    TRANSACTIONS_PAGE_SIZE,
} from '../../transactionsMeta';

export type TransactionsListLogic = {
    items: SalesTransaction[];
    totalItems: number;
    totalPages: number;
    page: number;
    rowsPerPage: number;
    pageWindow: (number | 'ellipsis')[];
    isLoading: boolean;
    error: ErrorResponse | null;
    onRetry: () => void;
    onPageChange: (next: number) => void;
    onRowsPerPageChange: (next: number) => void;
};

/**
 * Loads the selected day's transactions, **server-paged** — a day can hold
 * thousands of sales, so only one page is fetched at a time
 * (`useSalesTransactionsPageQuery`). Owns the page + rows-per-page state; the
 * page resets to 1 when the date or page size changes. `from === to === date`,
 * which the bos-api resolves to a single-day window.
 *
 * Scope: the bos-api list branch-scopes by the caller's token — a BranchAdmin
 * sees only their branch — so this hook sends nothing branch-related.
 */
export const useTransactionsList = (date: string): TransactionsListLogic => {
    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = React.useState(
        TRANSACTIONS_PAGE_SIZE,
    );

    // A new day starts at page 1 — the old page number is meaningless for it.
    React.useEffect(() => {
        setPage(1);
    }, [date]);

    const query = useSalesTransactionsPageQuery({
        from: date,
        to: date,
        page,
        pageSize: rowsPerPage,
    });
    const { data, refetch } = query;

    const envelope = data && data.success ? data.response : null;
    const items = envelope?.items ?? [];
    const totalItems = envelope?.totalCount ?? 0;
    const totalPages = envelope?.totalPages ?? 0;

    const error = React.useMemo<ErrorResponse | null>(
        () => (data && !data.success ? (data.error ?? null) : null),
        [data],
    );

    const pageWindow = React.useMemo(
        () => getPaginationWindow(totalPages, page),
        [totalPages, page],
    );

    const onRetry = React.useCallback(() => {
        void refetch();
    }, [refetch]);

    const onPageChange = React.useCallback((next: number) => {
        setPage(next);
    }, []);

    const onRowsPerPageChange = React.useCallback((next: number) => {
        setRowsPerPage(next);
        setPage(1);
    }, []);

    return {
        items,
        totalItems,
        totalPages,
        page,
        rowsPerPage,
        pageWindow,
        // `isPending` is only true on the first load; paging keeps the previous
        // page visible (keepPreviousData), so the skeleton shows once.
        isLoading: query.isPending,
        error,
        onRetry,
        onPageChange,
        onRowsPerPageChange,
    };
};
